import type { FlatSurface } from '@repo/api-contracts';
import type { PointerEvent, ReactElement } from 'react';
import {
  type PhotoDimensions,
  type Rect,
  type ResizeHandle,
  surfaceMmToPhoto,
} from '@/entities/edit-area';

interface ResizeHandlesOverlayProps {
  /** 선택된 영역의 외접 사각형(표면 mm). */
  readonly rect: Rect;
  readonly surface: FlatSurface;
  readonly photoSize: PhotoDimensions;
  /** 자유 다각형은 모서리 4개만, 사각형·원형은 모서리·변 8개를 보인다. */
  readonly cornersOnly: boolean;
  readonly disabled: boolean;
  /** 화면에서 잡을 수 있는 최소 한 변(사진 px). 사진을 줄여 보일 때 핸들이 너무 작아지지 않게 한다. */
  readonly minSizePx?: number;
  readonly onHandleStart: (event: PointerEvent<SVGElement>, handle: ResizeHandle) => void;
}

const CORNERS: readonly ResizeHandle[] = ['nw', 'ne', 'se', 'sw'];
const ALL: readonly ResizeHandle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
const CURSOR: Readonly<Record<ResizeHandle, string>> = {
  n: 'ns-resize',
  s: 'ns-resize',
  e: 'ew-resize',
  w: 'ew-resize',
  nw: 'nwse-resize',
  se: 'nwse-resize',
  ne: 'nesw-resize',
  sw: 'nesw-resize',
};

/** 선택된 평면 영역의 크기 핸들. 핸들 위치는 외접 사각형의 모서리·변 가운데다. */
export const ResizeHandlesOverlay = ({
  rect,
  surface,
  photoSize,
  cornersOnly,
  disabled,
  minSizePx = 0,
  onHandleStart,
}: ResizeHandlesOverlayProps): ReactElement => {
  const topLeft = surfaceMmToPhoto(surface, photoSize, { xMm: rect.xMm, yMm: rect.yMm });
  const bottomRight = surfaceMmToPhoto(surface, photoSize, {
    xMm: rect.xMm + rect.widthMm,
    yMm: rect.yMm + rect.heightMm,
  });
  const size = Math.max(8, minSizePx, Math.max(photoSize.widthPx, photoSize.heightPx) * 0.006);
  const xOf = (handle: ResizeHandle): number =>
    handle.includes('w')
      ? topLeft.x
      : handle.includes('e')
        ? bottomRight.x
        : (topLeft.x + bottomRight.x) / 2;
  const yOf = (handle: ResizeHandle): number =>
    handle.includes('n')
      ? topLeft.y
      : handle.includes('s')
        ? bottomRight.y
        : (topLeft.y + bottomRight.y) / 2;
  return (
    <g data-testid="resize-handles">
      {(cornersOnly ? CORNERS : ALL).map((handle) => (
        <rect
          key={handle}
          data-testid={`resize-handle-${handle}`}
          x={xOf(handle) - size / 2}
          y={yOf(handle) - size / 2}
          width={size}
          height={size}
          fill="white"
          stroke="#059669"
          strokeWidth={2}
          vectorEffect="non-scaling-stroke"
          pointerEvents="all"
          style={{ cursor: disabled ? 'default' : CURSOR[handle] }}
          onPointerDown={(event) => onHandleStart(event, handle)}
        />
      ))}
    </g>
  );
};
